<template>
  <mounting-portal mount-to="#vue-popups" append slim>
    <transition name="fade">
      <div v-if="modelValue" class="popup-photo-slider u-fixed-full">
        <div
          v-scroll-lock="modelValue"
          class="popup-photo-slider__container u-absolute-fill"
        >
          <div
            ref="swiper"
            class="popup-photo-slider__swiper swiper u-flex-grow"
            @click.self="close"
          >
            <div class="swiper-wrapper" @click.self="close">
              <div
                v-for="{ id, path, url } in photos"
                :key="id"
                class="popup-photo-slider__item swiper-slide"
              >
                <div class="popup-photo-slider__preloader u-absolute-center">
                  <preloader visible />
                </div>
                <img
                  v-lazy="path || url"
                  alt=""
                  class="popup-photo-slider__img"
                />
              </div>
            </div>
          </div>
          <div
            ref="pagination"
            class="popup-photo-slider__title u-absolute-center-x font-text"
          ></div>
          <div
            class="
              popup-photo-slider__navigation
              u-absolute-center u-stroke-deep-current u-text-white
            "
          >
            <template v-for="n in 2">
              <button
                :key="n"
                :ref="n === 1 ? 'prev' : 'next'"
                type="button"
                class="popup-photo-slider__navigation-button u-clickable"
              >
                <svg-inline
                  tag="span"
                  class="u-decor"
                  :dir="n === 1 ? 'left' : 'right'"
                >
                  <icon icon="icon-angle-up-mini" />
                </svg-inline>
              </button>
            </template>
          </div>
          <button
            type="button"
            class="popup-photo-slider__download u-clickable u-text-white"
            @click="download"
            v-if="User.state.userRole === 'FAKT'"
          >
            <svg-inline class="u-decor u-stroke-deep-current">
              <icon icon="icon-upload" />
            </svg-inline>
          </button>
          <button
            type="button"
            class="popup-photo-slider__close u-clickable u-text-white"
            @click="close"
          >
            <svg-inline class="u-decor u-stroke-deep-current">
              <icon icon="close-mini" />
            </svg-inline>
          </button>
        </div>
      </div>
    </transition>
  </mounting-portal>
</template>

<script>
import Swiper, { Navigation, Pagination, EffectFade, Keyboard } from "swiper";
import "swiper/swiper.scss";
import "swiper/modules/effect-fade/effect-fade.scss";
import Preloader from "@/components/ui-kit/preloader/index.vue";
import SvgInline from "@/components/ui-kit/svg-inline/index.vue";
import Icon from "@/components/icons/index.vue";
import User from "@/store/modules/user";

export default {
  computed: {
    User() {
      return User
    }
  },
  components: {
    Preloader,
    SvgInline,
    Icon,
  },
  model: {
    prop: "modelValue",
    event: "change",
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    photos: {
      type: Array,
      default() {
        return [];
      },
    },
    initialSlide: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      swiperInstance: null,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(visible) {
        if (visible) {
          setTimeout(this.initSlider);
          return;
        }

        this.destroySlider();
      },
    },
  },
  mounted() {
    fakeWindow.addEventListener("keydown", this.onEsc);
  },
  beforeDestroy() {
    this.destroySlider();
    fakeWindow.removeEventListener("keydown", this.onEsc);
  },
  methods: {
    initSlider() {
      this.swiperInstance = new Swiper(this.$refs.swiper, {
        touchReleaseOnEdges: true,
        observer: true,
        rewind: true,
        initialSlide: this.initialSlide,
        navigation: {
          nextEl: this.$refs.next,
          prevEl: this.$refs.prev,
        },
        pagination: {
          el: this.$refs.pagination,
          renderCustom: (swiper, current, total) =>
            this.$t("pagination.text", { current, total }),
          type: "custom",
        },
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        modules: [Navigation, Pagination, EffectFade, Keyboard],
      });
    },
    destroySlider() {
      this.swiperInstance?.destroy();
    },
    close() {
      this.$emit("close");
      this.$emit("change", false);
    },
    onEsc({ keyCode }) {
      if (keyCode === 27 && this.modelValue) {
        this.close();
      }
    },
    download() {
      let curIndex = this.swiperInstance?.activeIndex;
      let href = this.photos[curIndex]?.path || this.photos[curIndex]?.url;

      let xhr = new XMLHttpRequest();
      xhr.open('GET', href);
      xhr.responseType = 'blob';
      xhr.onload = () => {
        let link = document.createElement('a');
        link.href = window.URL.createObjectURL(xhr.response);
        link.download = href.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      }
      xhr.send();
    }
  },
};
</script>

<style lang="scss" scoped>

.swiper-slide {
  img {
    display: none;
  }

  &-active img{
    display: inline;
  }

  &-prev img {
    display: inline;
  }

  &-next img{
    display: inline;
  }
}
.popup-photo-slider {
  outline: none;
  z-index: 100000;
  -webkit-backface-visibility: hidden;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  color: var.$white;

  &::before {
    @include mixin.decor();
    content: "";
    position: absolute;
    top: -100px;
    right: 0;
    bottom: -100px;
    left: 0;
    background-color: rgba(var.$black, 0.9);
  }

  &__container {
    text-align: center;
  }

  &__title {
    top: 16px;
    bottom: auto;
    width: auto;
    z-index: 1;

    @include mixin.size-lg-min {
      top: 40px;
      font-size: 16px;
    }
  }

  &__swiper {
    height: 100%;
    width: 100%;
    cursor: pointer;
  }

  &__item {
    height: auto;
    text-align: center;
    white-space: nowrap;
    padding: 104px 0;
    pointer-events: none;

    @include mixin.size-lg-min {
      padding: 104px;
    }

    &::after {
      display: inline-block;
      content: "";
      vertical-align: middle;
      height: 100%;
      width: 0;
    }

    &.swiper-slide-active {
      z-index: 1;
    }
  }

  &__preloader {
    width: 100%;
  }

  &__img {
    position: relative;
    vertical-align: middle;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    pointer-events: auto;
    cursor: default;
  }

  &__navigation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    pointer-events: none;
    width: 100%;
    z-index: 2;
    padding: 0 52px;

    @include mixin.size-md {
      display: none;
    }

    &-button {
      @include mixin.button-reset();
      pointer-events: auto;
      color: var.$white;
      width: 32px;
      transition: var.$transition-default;
      position: relative;

      &.swiper-button-disabled {
        opacity: 0.5;
      }

      &:hover {
        &::after {
          opacity: 1;
        }
      }

      &::after {
        @include mixin.full-pos(percentage(func.divide(-12, 32)));
        position: absolute;
        display: block;
        content: "";
        background: rgba(var.$white, 0.07);
        border-radius: 50%;
        opacity: 0;
        transition: var.$transition-default;
      }
    }
  }

  &__close {
    @include mixin.button-reset();
    position: absolute;
    top: 21px;
    right: 21px;
    width: 20px;
    transition: var.$transition-default;
    z-index: 2;

    @include mixin.size-lg-min {
      top: 40px;
      right: 40px;
    }

    &:hover {
      &::after {
        opacity: 1;
      }
    }

    &::after {
      @include mixin.full-pos(percentage(func.divide(-18, 20)));
      position: absolute;
      display: block;
      content: "";
      background: rgba(var.$white, 0.07);
      border-radius: 50%;
      opacity: 0;
      transition: var.$transition-default;
    }
  }

  &__download {
    @include mixin.button-reset();
    position: absolute;
    top: 21px;
    left: 21px;
    width: 20px;
    transition: var.$transition-default;
    z-index: 2;
    stroke: #fff;

    @include mixin.size-lg-min {
      top: 40px;
      left: 40px;
    }

    &:hover {
      &::after {
        opacity: 1;
      }
    }

    &::after {
      @include mixin.full-pos(percentage(func.divide(-18, 20)));
      position: absolute;
      display: block;
      content: "";
      background: rgba(var.$white, 0.07);
      border-radius: 50%;
      opacity: 0;
      transition: var.$transition-default;
    }
  }
}
</style>
